import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { Zap } from "lucide-react";

/**
 * Quarter Mark Milestone Modal
 * 
 * A calm, premium celebration for completing 25 tasks.
 * Feels like a micro-win, not a gamified explosion.
 */

// Soft double-pulse sound
const playMilestoneSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playPulse = (time, volume = 0.12, pitch = 80) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(pitch, time);
      oscillator.frequency.exponentialRampToValueAtTime(pitch * 0.5, time + 0.15);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, time);
      gainNode.gain.linearRampToValueAtTime(volume, time + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, time + 0.2);
      
      oscillator.start(time);
      oscillator.stop(time + 0.25);
    };
    
    const now = audioContext.currentTime;
    playPulse(now, 0.14, 70);
    playPulse(now + 0.18, 0.1, 90);
    
  } catch (e) {
    // Audio not supported
  }
};

// Soft heartbeat haptic
const triggerMilestoneHaptic = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(60);
    setTimeout(() => navigator.vibrate(45), 180);
  }
};

// Generate ambient micro-particles
const generateAmbientParticles = () => {
  const count = 12;
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.3,
    distance: 60 + Math.random() * 40,
    size: 3 + Math.random() * 3,
    delay: 0.3 + (i * 0.06),
    duration: 1.2 + Math.random() * 0.4,
    color: [
      'rgba(249, 115, 22, 0.5)',   // Orange
      'rgba(251, 146, 60, 0.45)', // Light orange
      'rgba(239, 68, 68, 0.4)',   // Red
      'rgba(251, 191, 36, 0.45)', // Amber
      'rgba(254, 215, 170, 0.4)', // Peach
    ][Math.floor(Math.random() * 5)],
  }));
};

export const QuarterMarkModal = ({ isOpen, onClose }) => {
  const [particles, setParticles] = useState([]);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasPlayedRef.current) {
      hasPlayedRef.current = true;
      setParticles(generateAmbientParticles());
      
      // Delayed sound and haptic for cinematic feel
      setTimeout(() => {
        playMilestoneSound();
        triggerMilestoneHaptic();
      }, 200);
    }
    
    if (!isOpen) {
      hasPlayedRef.current = false;
    }
  }, [isOpen]);

  const handleDismiss = () => {
    triggerMilestoneHaptic();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - soft dim */}
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-[#FFFBF7] rounded-3xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.15)] max-w-sm mx-4 pointer-events-auto"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ambient particles */}
              <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
                {particles.map((particle) => (
                  <motion.div
                    key={particle.id}
                    className="absolute rounded-full"
                    style={{
                      width: particle.size,
                      height: particle.size,
                      left: '50%',
                      top: '35%',
                      backgroundColor: particle.color,
                    }}
                    initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                    animate={{
                      opacity: [0, 0.7, 0.4, 0],
                      scale: [0, 1, 0.8, 0.3],
                      x: Math.cos(particle.angle) * particle.distance,
                      y: Math.sin(particle.angle) * particle.distance,
                    }}
                    transition={{
                      duration: particle.duration,
                      delay: particle.delay,
                      ease: [0.1, 0.2, 0.3, 1],
                    }}
                  />
                ))}
              </div>

              {/* Icon with ripple */}
              <div className="relative flex justify-center mb-6">
                {/* Slow ripple behind icon */}
                <motion.div
                  className="absolute w-20 h-20 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(249, 115, 22, 0.15) 0%, transparent 70%)',
                  }}
                  animate={{
                    scale: [1, 1.8, 2.2],
                    opacity: [0.4, 0.2, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: 1,
                    ease: [0.25, 0.1, 0.3, 1],
                  }}
                />
                <motion.div
                  className="absolute w-20 h-20 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, transparent 60%)',
                  }}
                  animate={{
                    scale: [1, 1.5, 1.8],
                    opacity: [0.3, 0.15, 0],
                  }}
                  transition={{
                    duration: 1.8,
                    delay: 0.3,
                    repeat: 1,
                    ease: [0.25, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Glowing energy icon */}
                <motion.div
                  className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #DC2626 100%)',
                    boxShadow: '0 4px 20px rgba(249, 115, 22, 0.4)',
                  }}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <Zap className="text-white" size={28} strokeWidth={2.5} fill="rgba(255,255,255,0.3)" />
                </motion.div>
              </div>

              {/* Text content */}
              <div className="text-center relative z-10">
                <motion.h2
                  className="text-2xl font-bold text-[#1A1A1C] mb-1"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                >
                  Quarter Mark!
                </motion.h2>
                
                <motion.p
                  className="text-base text-[#6B6B6B] mb-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  25 tasks completed
                </motion.p>
                
                <motion.p
                  className="text-sm text-[#8B8B8B] mb-1"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                >
                  Your momentum is building.
                </motion.p>
                
                <motion.p
                  className="text-xs text-[#B5B5B5] mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                >
                  Progress, not pressure.
                </motion.p>

                {/* Primary button */}
                <motion.button
                  className="relative px-8 py-3 rounded-full text-white font-semibold text-base overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #DC2626 100%)',
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: '0 6px 25px rgba(249, 115, 22, 0.45)',
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDismiss}
                  data-testid="milestone-continue-btn"
                >
                  <span className="relative z-10">Keep it going 🔥</span>
                  
                  {/* Subtle glow on hover */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, transparent 70%)',
                    }}
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuarterMarkModal;
